import { useEffect, useMemo, useRef } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import { format, isToday } from 'date-fns'

import { getNameInitials } from '@/helpers/getNameInitials'
import { Code, Log, MandatoryLogInformation, RouteSelectionMode } from '@/types/logs'
import { useGetUserInfo } from '@/modules/Auth'
import { CommonHeader, filterByTimeRange, tableCellStyles, TimeHeader } from '@/ui/Table'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useCombinedLogsTableStateStore } from '../store'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { COMBINED_LOGS_COLUMNS } from '../constant'

import { formatCodeFilterValues } from '../helpers/formatCodeFilterValues'
import { formatRouteFilterValues } from '../helpers/formatRouteFilterValues'
import { formatRunFilterValues } from '../helpers/formatRunFilterValues'
import { formatDutyFilterValues } from '../helpers/formatDutyFilterValues'
import { formatFleetFilterValues } from '../helpers/formatFleetFilterValues'
import { sortLogsByLocked } from '../helpers/sortLogsByLocked'
import { sortLogsByUrgency } from '../helpers/sortLogsByUrgency'
import { sortLogsBy } from '../helpers/sortLogsBy'

import { Code as CodeView } from '../ui/components/information/Code'
import { Location } from '../ui/components/information/Location'
import { LogAction } from '../ui/components/information/LogAction'
import { Actions } from '../ui/components/Actions'
import { AuthorTooltip } from '../ui/components/TableForm/tooltips/AuthorTooltip'
import { Unfinished } from '../ui/components/information/Unfinished'
import { FlaggedCell } from '../ui/components/information/FlaggedCell'
import { UnfinishedHeader } from '../ui/components/UnfinishedHeader'
import { Reference } from '../ui/components/information/Reference'
import { Routes } from '../ui/components/information/Routes'
import { Trip } from '../ui/components/information/Trip'
import { TripNestedInfo } from '../ui/components/information/TripNestedInfo'
import { Description } from '../ui/components/information/Description'
import { Lock } from '../ui/components/Lock'
import { LockHeader } from '@/ui/Table/ui/components/LockHeader'
import { filterLogsBy } from '../helpers/filterLogsBy'
import { ReaderLogAction } from '../ui/components/information/ReaderLogAction'
import { useCachedGetCodeName } from './useCachedGetCodeName'
import { useManageLogOriginalRoute } from './useManageLogOriginalRoute'
import { useBusStops } from '@/pages/UnfinishedRecords/hooks/useBusStops'
import { useNightShiftInfo } from '@/modules/Auth/helpers/useNightShiftInfo'
import { useDateStore } from '@/components/DateInput'
import FlagIcon from '../assets/flag.svg'

const Time = () => {
    const currentDate = useRef(new Date())

    useEffect(() => {
        const timerId = setInterval(() => (currentDate.current = new Date()), 1000)

        return () => {
            clearInterval(timerId)
        }
    }, [])

    return <>{format(currentDate.current, 'HH:mm')}</>
}

export const useCombinedLogsColumns = (isDataLoading: boolean, codes: Code[]) => {
    const columnHelper = createColumnHelper<Log>()
    const { filter } = useCombinedLogsTableStateStore()
    const { role } = useAuthStore()

    const { data: userInfo } = useGetUserInfo()
    const hasPermission = useCheckPermissions()
    const getCodeName = useCachedGetCodeName()
    const { getElementAttributes } = useManageLogOriginalRoute(isDataLoading)
    const { getRouteBusStops, allStops: allStopsData } = useBusStops()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()
    const { date } = useDateStore()
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const theDate = new Date(date)
    let isTodayVal = isToday(theDate)
    if (isNightShift && isAfterMidnight()) {
        isTodayVal = isToday(new Date(theDate).setDate(theDate.getDate() + 1))
    }
    const limitToTime = useMemo(() => (isTodayVal ? new Date() : undefined), [isTodayVal])

    return useMemo(
        () => [
            columnHelper.accessor('code', {
                id: COMBINED_LOGS_COLUMNS.Code,
                header: (context) => (
                    <CommonHeader
                        label="Code"
                        buttonLabel="Sort by code"
                        searchPlaceholder="Type to search for code"
                        {...context}
                    />
                ),
                cell: (info) => <CodeView {...info} getCodeName={getCodeName} />,
                filterFn: filterLogsBy((log) => !log.code),
                meta: {
                    isPlaceholder: true,
                    placeholder: 'Code',
                    alignCenter: true,
                    width: 'var(--size-128)',
                    formatFilterValues: (values) => formatCodeFilterValues(values, filter.type, codes),
                },
            }),
            columnHelper.accessor('reference', {
                id: COMBINED_LOGS_COLUMNS.Reference,
                header: 'Reference',
                cell: (info) => <Reference {...info} isDataLoading={isDataLoading} />,
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                    isPlaceholder: true,
                    placeholder: '-',
                },
            }),
            columnHelper.accessor('routes', {
                id: COMBINED_LOGS_COLUMNS.Routes,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by routes"
                        searchPlaceholder="Type to search for routes"
                        label="Route"
                    />
                ),
                cell: (info) => <Routes {...info} isDataLoading={isDataLoading} />,
                filterFn: filterLogsBy((log) => log.routeSelectionMode === RouteSelectionMode.None),
                sortingFn: sortLogsBy(
                    COMBINED_LOGS_COLUMNS.Routes,
                    (log) => log.routeSelectionMode === RouteSelectionMode.None
                ),
                meta: {
                    alignCenter: true,
                    width: 'var(--size-128)',
                    isPlaceholder: true,
                    placeholder: 'Route',
                    formatFilterValues: (values, table) => formatRouteFilterValues(values, table, filter.type),
                },
            }),
            columnHelper.accessor('trip', {
                id: COMBINED_LOGS_COLUMNS.Trip,
                header: 'Trip',
                cell: (info) => (
                    <Trip {...info} isDataLoading={isDataLoading} getElementAttributes={getElementAttributes} />
                ),
                meta: {
                    alignCenter: true,
                    width: 'var(--size-80)',
                },
            }),
            columnHelper.accessor('run', {
                id: COMBINED_LOGS_COLUMNS.Run,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by run"
                        searchPlaceholder="Type to search for run"
                        label="Run"
                    />
                ),
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Run}
                        label="run"
                        isDataLoading={isDataLoading}
                        getBulkCurtailmentValue={(log) => log.bulkCurtailment?.runTo || ''}
                        {...info}
                    />
                ),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Run, (log) => !log.run),
                filterFn: filterLogsBy((log) => !log.run),
                meta: {
                    alignCenter: true,
                    width: 'var(--size-80)',
                    isPlaceholder: true,
                    placeholder: 'Run',
                    formatFilterValues: (values, table) => formatRunFilterValues(values, table, filter.type),
                },
            }),
            columnHelper.accessor('duty', {
                id: COMBINED_LOGS_COLUMNS.Duty,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by duty"
                        searchPlaceholder="Type to search for duty"
                        label="Duty"
                    />
                ),
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Duty}
                        label="duty"
                        isDataLoading={isDataLoading}
                        formatValue={(value) => (value as string[]).join('/')}
                        getBulkCurtailmentValue={(log) => log.bulkCurtailment?.dutyTo || ''}
                        {...info}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.duty.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Duty, (log) => !log.duty?.length),
                meta: {
                    alignCenter: true,
                    width: 'var(--size-80)',
                    isPlaceholder: true,
                    placeholder: 'Duty',
                    formatFilterValues: (values, table) => formatDutyFilterValues(values, table, filter.type),
                },
            }),
            columnHelper.accessor('fleet', {
                id: COMBINED_LOGS_COLUMNS.Fleet,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by fleet"
                        searchPlaceholder="Type to search for fleet"
                        label="Fleet"
                    />
                ),
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Fleet}
                        label="fleet"
                        isDataLoading={isDataLoading}
                        formatValue={(value) => (value as string[]).join('/')}
                        getBulkCurtailmentValue={(log) => log.bulkCurtailment?.fleetTo || ''}
                        {...info}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.fleet?.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Fleet, (log) => !log.fleet?.length),
                meta: {
                    alignCenter: true,
                    width: 'var(--size-80)',
                    isPlaceholder: true,
                    placeholder: 'Fleet',
                    formatFilterValues: (values, table) => formatFleetFilterValues(values, table, filter.type),
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    locationFrom: row.locationFrom,
                    locationTo: row.locationTo,
                    locationType: row.locationType,
                    direction: row.direction,
                }),
                {
                    id: COMBINED_LOGS_COLUMNS.Location,
                    header: 'Location',
                    cell: (info) => (
                        <Location {...info} getRouteBusStops={getRouteBusStops} allStopsData={allStopsData} />
                    ),
                    meta: {
                        width: '100%',
                    },
                }
            ),
            columnHelper.accessor('descriptionInfo', {
                id: COMBINED_LOGS_COLUMNS.Description,
                header: 'Description',
                cell: (info) => <Description {...info} />,
                meta: {
                    width: '100%',
                },
            }),
            columnHelper.accessor('delayInMinutes', {
                id: COMBINED_LOGS_COLUMNS.Delay,
                header: 'Delay',
                cell: (info) => {
                    const value = info.getValue()

                    if (!value) {
                        return '-'
                    }

                    return <span className={tableCellStyles.greyCell}>{`${value} ${!isNaN(value) ? 'min' : ''}`}</span>
                },
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('mileage', {
                id: COMBINED_LOGS_COLUMNS.Milage,
                header: 'Mileage',
                cell: (info) => (
                    <span className={tableCellStyles.greyCell}>
                        {info.row.original.isZeroMileage ? '0.00' : info.getValue() ? info.getValue() : '-'}
                    </span>
                ),
                meta: {
                    alignCenter: true,
                    isPlaceholder: true,
                    placeholder: '-',
                },
            }),
            columnHelper.accessor('author', {
                id: COMBINED_LOGS_COLUMNS.Author,
                header: 'Author',
                cell: (info) => {
                    const displayAuthor = info.row.original.originalAuthor || info.getValue()
                    return (
                        <AuthorTooltip author={displayAuthor || '-'}>
                            <span className={tableCellStyles.greyCell}>
                                {displayAuthor ? getNameInitials(displayAuthor) : '-'}
                            </span>
                        </AuthorTooltip>
                    )
                },
                meta: {
                    alignCenter: true,
                    isPlaceholder: true,
                    placeholder: getNameInitials(userInfo?.userDetails.name || userInfo?.lastSessionDetails.authorName),
                },
            }),
            columnHelper.accessor('timestamp', {
                id: COMBINED_LOGS_COLUMNS.Time,
                header: (context) => (
                    <TimeHeader
                        {...context}
                        label="Time"
                        buttonLabel="Sort by time"
                        includePreviousDay={isNightShift && isAfterMidnight()}
                        limitToTime={limitToTime}
                    />
                ),
                cell: (info) => (
                    <span className={tableCellStyles.greyCell}>
                        {formatInTimeZone(info.getValue(), timezone, 'HH:mm')}
                    </span>
                ),
                filterFn: filterByTimeRange,
                meta: {
                    alignCenter: true,
                    isPlaceholder: true,
                    width: '5rem',
                    placeholder: <Time />,
                },
            }),
            columnHelper.accessor((row) => row.status, {
                id: COMBINED_LOGS_COLUMNS.Unfinished,
                header: (context) => <UnfinishedHeader {...context} />,
                cell: ({ row }) => {
                    if (hasPermission('log', 'manageUnfinished')) {
                        return <Unfinished log={row.original} />
                    }

                    if (role === Role.Reader) {
                        return <ReaderLogAction row={row} />
                    }
                    return <LogAction log={row.original} />
                },
                enableSorting: true,
                sortingFn: sortLogsByUrgency(role || Role.Controller),
                meta: {
                    isPlaceholder: true,
                    width: '10.285rem',
                    alignCenter: true,
                },
            }),
            columnHelper.display({
                id: COMBINED_LOGS_COLUMNS.Flag,
                header: () => (
                    <span className={tableCellStyles.iconHeader}>
                        <FlagIcon />
                    </span>
                ),
                cell: ({ row }) => <FlaggedCell log={row.original} />,
                meta: {
                    alignCenter: true,
                    width: '5rem',
                },
            }),
            columnHelper.display({
                id: COMBINED_LOGS_COLUMNS.LogActions,
                cell: ({ row }) => {
                    if (role === Role.Reader) {
                        return <ReaderLogAction row={row} />
                    }

                    return <LogAction log={row.original} />
                },
                enableSorting: true,
                sortingFn: sortLogsByUrgency(role || Role.Controller),
                meta: {
                    isPlaceholder: true,
                    width: '10.285rem',
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('isLocked', {
                header: (context) => <LockHeader {...context} />,
                id: COMBINED_LOGS_COLUMNS.Lock,
                cell: ({ row }) => <Lock id={row.original.id} log={row.original} />,
                enableSorting: true,
                sortingFn: sortLogsByLocked,
                meta: {
                    isPlaceholder: true,
                    alignCenter: true,
                },
            }),
            columnHelper.display({
                id: COMBINED_LOGS_COLUMNS.Actions,
                cell: (context) => <Actions {...context} />,
                meta: {
                    width: 'var(--size-40)',
                },
            }),
        ],
        [isDataLoading, filter, allStopsData]
    )
}
